export const TERMINAL_TOURNAMENT_STATUSES = new Set([
  "finished",
  "completed",
  "cancelled",
  "canceled",
  "did_not_start",
  "void",
]);

const ACTIVE_TOURNAMENT_PHASE_STATUSES = new Set(["running", "preparing", "ready"]);
const NOT_STARTED_TERMINAL_STATUSES = new Set(["did_not_start", "startet_nicht", "not_started"]);
const FINISHED_TOURNAMENT_STATUSES = new Set(["finished", "completed", "void"]);
const ACTUALLY_FINISHED_TOURNAMENT_STATUSES = new Set(["finished", "completed"]);
const TOURNAMENT_FINISHED_WINDOW_MS = 24 * 60 * 60 * 1000;
const PARTICIPANT_FIELDS = ["players", "playerUids", "participantUids", "registeredPlayerUids", "participants"];

function hasOwnValue(value, key) {
  return !!value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, key);
}

function entryMatchesUid(entry, uid) {
  if (!entry || !uid) return false;
  if (String(entry) === String(uid)) return true;
  if (typeof entry !== "object") return false;
  return [entry.uid, entry.userId, entry.playerUid, entry.id].some((value) => String(value || "") === String(uid));
}

function fieldContainsUid(value, uid) {
  if (!value || !uid) return false;
  if (Array.isArray(value)) return value.some((entry) => entryMatchesUid(entry, uid));
  if (typeof value !== "object") return String(value) === String(uid);
  if (hasOwnValue(value, uid)) return true;
  return Object.values(value).some((entry) => entryMatchesUid(entry, uid));
}

function tournamentParticipantFieldsContainUid(tournament, currentUid) {
  return PARTICIPANT_FIELDS.some((field) => fieldContainsUid(tournament?.[field], currentUid));
}

export function getTournamentTimestampMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value.seconds === "number") {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1_000_000);
  }
  return 0;
}

export function isTournamentTerminal(tournament) {
  const status = String(tournament?.status || "").toLowerCase();
  return (
    TERMINAL_TOURNAMENT_STATUSES.has(status) ||
    !!tournament?.winnerUid ||
    !!tournament?.deletedAt ||
    !!tournament?.tournamentDeletedAt
  );
}

export function isTournamentParticipant(tournament, currentUid) {
  if (!currentUid || !tournament) return false;
  const joinedAt = tournament.joinedAt || {};
  const leftAt = tournament.leftAt || {};
  const isParticipant =
    tournamentParticipantFieldsContainUid(tournament, currentUid) ||
    hasOwnValue(joinedAt, currentUid);
  const hasLeft = hasOwnValue(leftAt, currentUid);

  return isParticipant && !hasLeft;
}

export function isCurrentTournamentParticipant(tournament, currentUid) {
  return isTournamentParticipant(tournament, currentUid);
}

export function hasTournamentActuallyStarted(tournament, nowMillis = Date.now()) {
  if (!tournament) return false;
  const status = String(tournament.status || "open").toLowerCase();
  if (NOT_STARTED_TERMINAL_STATUSES.has(status)) return false;
  if (status === "running" || status === "finished" || status === "completed") return true;
  if (tournament.startedAt || tournament.groupStageGeneratedAt || tournament.groupStageGenerated === true) return true;

  const startAtMillis = getTournamentTimestampMillis(tournament.startAt || tournament.scheduledStartAt);
  return startAtMillis > 0 && startAtMillis <= nowMillis && status !== "open";
}

export function getTournamentFinishedAtMillis(tournament) {
  return getTournamentTimestampMillis(
    tournament?.finishedAt ||
      tournament?.completedAt ||
      tournament?.voidedAt ||
      tournament?.endedAt ||
      tournament?.cancelledAt ||
      tournament?.canceledAt
  );
}

export function isReachableParticipantTournament(tournament, currentUid, nowMillis = Date.now()) {
  if (!isTournamentParticipant(tournament, currentUid) || isTournamentTerminal(tournament)) return false;

  const status = String(tournament.status || "open").toLowerCase();
  if (status === "running") return true;

  const groupStageStatus = String(tournament.groupStageStatus || "").toLowerCase();
  const knockoutStatus = String(tournament.knockoutStatus || "").toLowerCase();
  if (ACTIVE_TOURNAMENT_PHASE_STATUSES.has(groupStageStatus)) return true;
  if (ACTIVE_TOURNAMENT_PHASE_STATUSES.has(knockoutStatus)) return true;

  const startAtMillis = getTournamentTimestampMillis(tournament.startAt || tournament.scheduledStartAt);
  return startAtMillis > 0 && startAtMillis <= nowMillis && status !== "open";
}

export function isUpcomingParticipantTournament(tournament, currentUid, nowMillis = Date.now()) {
  if (!isTournamentParticipant(tournament, currentUid) || isTournamentTerminal(tournament)) return false;
  if (isReachableParticipantTournament(tournament, currentUid, nowMillis)) return false;

  const status = String(tournament.status || "open").toLowerCase();
  if (status !== "open" && status !== "pending" && status !== "full" && status !== "closed") return false;

  const startAtMillis = getTournamentTimestampMillis(tournament.startAt || tournament.scheduledStartAt);
  return startAtMillis === 0 || startAtMillis > nowMillis;
}

export function isRecentlyFinishedParticipantTournament(
  tournament,
  currentUid,
  nowMillis = Date.now(),
  windowMillis = TOURNAMENT_FINISHED_WINDOW_MS
) {
  if (!isTournamentParticipant(tournament, currentUid)) return false;

  const status = String(tournament?.status || "").toLowerCase();
  if (NOT_STARTED_TERMINAL_STATUSES.has(status)) return false;
  if (!hasTournamentActuallyStarted(tournament, nowMillis)) return false;

  const isFinal = FINISHED_TOURNAMENT_STATUSES.has(status) || status === "cancelled" || status === "canceled" || !!tournament?.winnerUid;
  if (!isFinal) return false;

  const finishedAtMillis = getTournamentFinishedAtMillis(tournament);
  if (finishedAtMillis <= 0) return false;

  return nowMillis - finishedAtMillis >= 0 && nowMillis - finishedAtMillis <= windowMillis;
}

export function isRunningTournament(tournament) {
  if (!tournament) return false;
  const status = String(tournament.status || "open").toLowerCase();
  return status === "running" && !isTournamentTerminal(tournament);
}

export function isUpcomingCurrentParticipantTournament(tournament, currentUid, nowMillis = Date.now()) {
  if (!isCurrentTournamentParticipant(tournament, currentUid) || isTournamentTerminal(tournament)) return false;

  const status = String(tournament.status || "open").toLowerCase();
  if (status !== "open") return false;
  if (hasTournamentActuallyStarted(tournament, nowMillis)) return false;

  const startAtMillis = getTournamentTimestampMillis(tournament.startAt || tournament.scheduledStartAt);
  return startAtMillis === 0 || startAtMillis > nowMillis;
}

export function isRecentlyFinishedTournament(
  tournament,
  nowMillis = Date.now(),
  windowMillis = TOURNAMENT_FINISHED_WINDOW_MS
) {
  if (!tournament) return false;

  const status = String(tournament.status || "").toLowerCase();
  if (NOT_STARTED_TERMINAL_STATUSES.has(status)) return false;
  if (!hasTournamentActuallyStarted(tournament, nowMillis)) return false;

  const isFinal = ACTUALLY_FINISHED_TOURNAMENT_STATUSES.has(status) || !!tournament.winnerUid;
  if (!isFinal) return false;

  const finishedAtMillis = getTournamentFinishedAtMillis(tournament);
  if (finishedAtMillis <= 0) return false;

  return nowMillis - finishedAtMillis >= 0 && nowMillis - finishedAtMillis <= windowMillis;
}

export function shouldShowActiveReachableTournamentCard({
  tournament,
  currentUid,
  joinDocId,
  visibleDocIds,
  nowMillis = Date.now(),
}) {
  if (!joinDocId) return false;
  const visibleSet = visibleDocIds instanceof Set ? visibleDocIds : new Set(visibleDocIds || []);
  if (visibleSet.has(joinDocId)) return false;
  return isReachableParticipantTournament(tournament, currentUid, nowMillis);
}
